(ns ductile.ui.edi
  "Grab-bag of UIs for EDI. All prototypes for now."
  (:require [clojure.pprint :as pprint]
            [clojure.string :as str]
            [ductile.clerk.viewer :as ductile.viewer]
            [ductile.ui.components :as ui]
            [ductile.ui.components.columns :as columns]
            [ductile.ui.components.omnibox :as omnibox]
            [ductile.ui.components.table :as table]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.util :as util]
            [ductile.ui.vehicle-search :as vehicle-search]
            [nextjournal.viewer :as v]
            [re-frame.context :as re-frame :refer [defc]]
            [reagent.core :as r]))


(def max-height "max(600px, calc(100vh - 213px - 40px))")

(defc edifact-display [sgmts]
  [:pre.overflow-y-auto
   {:style {:max-height max-height}}
   [v/inspect (v/view-as :code (with-out-str (pprint/pprint sgmts)))]])

(defn dispo-vin-search-relative-url [vin]
  (str "/as400/dispo?q=(!transport-list+*XXXXX+*YYY+*" vin "+i1)"))

(defn edifact-extracted [extracted]
  (r/with-let [instruction (r/atom nil)]
    [:div.flex
     [:div.flex-auto.overflow-y-auto.shadow-sm.rounded-sm
      {:style {:max-height max-height}}
      [ui/data-table
       [:thead [:tr
                [:th "VIN"]
                [:th "Message Ref/Name/ID"]
                [:th "Message type"]
                [:th "Destination"]
                [:th "Status Change"]]]
       (into [:tbody]
             (map-indexed (fn [i instr]
                            [:tr.cursor-pointer
                             {:on-click #(reset! instruction (nth (:instructions (:data @(re-frame/subscribe [:edi/current-file]))) i))}
                             [:td [:a {:href (dispo-vin-search-relative-url (:edifact/vin instr))}
                                   (:edifact/vin instr)]]
                             [:td (str (:edifact/message-ref instr) "\n"
                                       (:edifact/message-name instr) "\n"
                                       (:edifact/message-id instr))]
                             [:td (:edifact/message-type instr)]
                             [:td (:edifact/destination instr)]
                             [:td (when-let [s (:edifact/date-of-status-change instr)]
                                    (util/render-iso-localdatetime s))]])
                          extracted))]]
     (when-let [inst @instruction]
       [:div
        [:div.shadow-sm.rounded-sm.bg-white.border.ml-5.overflow-y-auto
         {:style {:width 300 :max-height max-height}}
         [:div.border-b.pl-5.pr-3.py-2.flex.justify-between.items-center.bg-gray-50
          [:span.font-bold "Instruction"]
          [:div.flex.items-center.justify-center.text-gray-400.hover:text-gray-500.cursor-pointer
           {:on-click #(reset! instruction nil)}
           [ui/icon :x]]]
         [:div.px-5.py-2
          [edifact-display inst]]]])]))

(defc edi-formats [fmt-kws]
  (let [last-i (dec (count fmt-kws))]
    (into [:span.relative.z-0.inline-flex.shadow-sm.rounded-md.mb-8]
          (map-indexed
           (fn [i fmt-kw]
             [:button.relative.inline-flex.items-center.px-4.py-2.border.bg-white.text-base.leading-5.font-medium.focus:z-10.focus:outline-none.active:bg-gray-100.active:text-gray-700.transition.ease-in-out.duration-150
              {:on-click #(re-frame/dispatch [:edi/display-format fmt-kw])
               :class (str (if (= fmt-kw @(re-frame/subscribe [:edi/display-format]))
                             "bg-green-500 text-white border-green-600 "
                             "text-gray-700 border-gray-300 ")
                           (when (zero? i)
                             "rounded-l-lg ")
                           (when-not (zero? i)
                             "-ml-1 ")
                           (when (= i last-i)
                             "rounded-r-lg "))}
              (str/capitalize (name fmt-kw))])
           fmt-kws))))

(defc vda-normalized [normed]
  [:div.mb-3
   (for [m normed]
     ^{:key (str m)}
     [:div
      (into [:div.border-b.mb-2.pb-2.monospace.text-sm]
            (map-indexed (fn [i [k v]]
                           ^{:key (str i k v)}
                           [:div.flex
                            [:div.font-medium.flex-shrink-0.text-right.pr-3.py-1.border-r
                             {:style {:width 320 :color "#166948"}}
                             (name k)]
                            [:div.flex-auto.pl-3.py-1 (str v)]])
                         m))])])

(defn inspect-formats [data formats active-format]
  [:<>
   [:div.flex.justify-end
    (into [:span.segmented-control]
          (map (fn [fmt]
                 [:button.segment
                  {:on-click #(reset! active-format fmt)
                   :class (when (= @active-format fmt) "active")}
                  (-> fmt name str/capitalize)])
               formats))]
   [:div.mt-3
    (let [{:keys [raw raw-lines parsed normalized extracted described] :as m} data]
      (case @active-format
        :raw [:pre.bg-gray-100.p-5.rounded.text-sm.border.overflow-auto
              {:style {:max-height max-height}}
              raw]
        :raw-lines [:pre.bg-gray-100.p-5.rounded.text-sm.border.overflow-auto
                    {:style {:max-height max-height}}
                    (for [line raw-lines]
                      [:p (str line)])]
        :adesa [:div.mb-3
                ;; TODO this was extracted from `vda-normalized` and should be generalized to a map viewer
                (for [m (:adesa m)]
                  ^{:key (str m)}
                  [:div
                   (into [:div.border-b.mb-2.pb-2.monospace.text-sm]
                         (map-indexed (fn [i [k v]]
                                        ^{:key (str i k v)}
                                        [:div.flex
                                         [:div.font-medium.flex-shrink-0.text-right.pr-3.py-1.border-r
                                          {:style {:width 320 :color "#166948"}}
                                          (name k)]
                                         [:div.flex-auto.pl-3.py-1 (str v)]])
                                      m))])]
        :parsed [:div.bg-gray-100.p-5.rounded.text-sm.border.overflow-auto
                 {:style {:max-height max-height}}
                 [edifact-display parsed]]
        :normalized [:div.bg-gray-100.p-5.rounded.text-sm.border.overflow-auto
                     {:style {:max-height max-height}}
                     [vda-normalized normalized]]
        :described [:div.bg-gray-100.p-5.rounded.text-sm.border.overflow-auto
                    {:style {:max-height max-height}}
                    [edifact-display described]]
        :extracted [:div.bg-gray-100.p-5.rounded.text-sm.border.overflow-auto
                    {:style {:max-height max-height}}
                    [edifact-extracted extracted]]))]])

(defn search!
  {::deframe/event true}
  [{:keys [db]} params]
  (if (empty? (-> params :search-params :vehicle/vin))
    {}
    {:db (deframe/process-tx db [[:op/assoc ::params params]])
     ::deframe/call-lambda [{:result-path [::results]}
                            'ductile.edi.dashboard/search
                            params]
     ::vehicle-search/query-params->url! [params]}))

(defn commit-value! [!search-params k v]
  (deframe/dispatch! `search! {:search-params (assoc @!search-params k v)}))

(defn read-query-params []
  (vehicle-search/parse-query-params (:query-params @deframe/!current-route)))

(defn read-params []
  (or @(deframe/!get ::params)
      (read-query-params)
      {}))

#_(read-params)

(def !params
  (r/track read-params))

(defn filter-edi-messages [filters messages]
  (let [filter-fns (->> filters
                        (keep (fn [[path filter-val]]
                                (let [path (if (vector? path) path [path])]
                                  (cond
                                    (= :filter/omnibox (:filter (meta filter-val)))
                                    #(omnibox/matches? (get-in % path) filter-val)

                                    :else
                                    (when-not (empty? filter-val)
                                      #(contains? filter-val (get-in % path))))))))]

    (cond->> messages
      (not-empty filter-fns)
      (filterv (apply every-pred filter-fns)))))

(defn edi-dashboard [{:keys [messages column-order search-params]} _match]
  [:<>
   [:div.mb-3
    [vehicle-search/vin-search
     (r/cursor !params [:search-params])
     :vehicle/vin {:label (tr "VINs")
                   :commit-value! commit-value!}]]
   (let [{:keys [result error loading]} @(deframe/!get ::results)]
     (cond
       loading
       [ui/loading-spinner]

       error
       [ui/error-view error]

       result
       (let [{:keys [messages total column-order hide-columns]} result
             filtered-messages (filter-edi-messages @(deframe/!route-get-in [:expanded-at :filters]) messages)

             available-cols (into (into (set hide-columns) (set column-order))
                                  (reduce (fn [acc m] (into acc (keys m))) #{} messages))
             col-settings @(deframe/!localstorage-get ::col-settings column-order)]
         [:<>
          [:div.flex.justify-between.pb-2.px-2.text-xs
           [:div (tr "Showing :showing of :total results" {:showing (count filtered-messages) :total total})
            (when (seq @(deframe/!route-get :expanded-at))
              [:span.pl-3 [ui/link {:on-click #(deframe/route-dissoc! :expanded-at)} (tr "Clear filters")]])]

           [columns/select-profile-dropdown {:col-settings col-settings
                                             :on-change #(deframe/localstorage-assoc! ::col-settings %)
                                             :on-modal-open #(deframe/route-assoc! ::col-settings-modal? true)}]]

          (when @(deframe/!route-get ::col-settings-modal?)
            [columns/select-columns-modal
             {:col-settings col-settings
              :available-cols available-cols
              :default-selected-cols column-order
              :on-close #(deframe/route-dissoc! ::col-settings-modal?)
              :on-change (fn [new-col-settings]
                           (deframe/localstorage-assoc! ::col-settings new-col-settings)
                           (deframe/route-dissoc! ::col-settings-modal?))}])

          [:div.w-full.overflow-auto.rounded.border.border-slate-300.shadow {:style {:max-height "calc(100vh - 118px)"}}
           [table/table
            (merge {:!expanded-at (deframe/!route-get :expanded-at)
                    :expanded-at-update-in! (fn [path f & args]
                                              (apply deframe/route-update-in! (into [:expanded-at] path) f args))
                    :group-headers false
                    :column-layout {}
                    :filters (into {}
                                   (for [[k opts] [[:edi-instruction/state]
                                                   [:eurolog.bmw/type]
                                                   [:eurolog.bmw/subtype]

                                                   [:eurolog.bmw/document-id {:numeric? true}]

                                                   #_[:eurolog.bmw/cancellation] ; TODO fix bool filters
                                                   #_[:eurolog.bmw/blocked]

                                                   [:eurolog.bmw/timestamp {:date? true}]
                                                   [:eurolog.bmw/status-date {:date? true}]
                                                   [:eurolog.bmw/completion-date {:date? true}]
                                                   [:eurolog.bmw/arrival-date {:date? true}]
                                                   [:eurolog.bmw/confirmed-pickup-date {:date? true}]

                                                   [:vehicle/vin]
                                                   [:eurolog.bmw/loading-point]
                                                   [:eurolog.bmw/unloading-point]
                                                   [:eurolog.bmw/interchange-id]
                                                   [:eurolog.bmw/final-destination]
                                                   [:eurolog.bmw/loading-list]
                                                   [:eurolog.bmw/vehicle-usage]
                                                   [:eurolog.bmw/transport-mode-requirements]
                                                   [:eurolog.bmw/transport-means-id]

                                                   [:eurolog.bmw/order {:numeric? true}]
                                                   [:eurolog.bmw/forwarder {:numeric? true}]
                                                   [:eurolog.bmw/transport-mode {:numeric? true}]
                                                   [:eurolog.bmw/transport-status {:numeric? true}]
                                                   [:eurolog.bmw/ultimate-customer {:numeric? true}]
                                                   [:eurolog.bmw/ship-from {:numeric? true}]
                                                   [:eurolog.bmw/ship-to {:numeric? true}]]]
                                     [k [:omnibox
                                         (cond
                                           (:date? opts) (into (sorted-set)
                                                               (map (fn [m] (omnibox/date->day (if (vector? k) (get-in m k) (get m k)))))
                                                               messages)
                                           :else (into (sorted-set)
                                                       (map (if (vector? k) #(get-in % k) #(get % k)))
                                                       messages))


                                         opts]]))

                    :transform-columns {[:eurolog.bmw/loading-point-address]
                                        (comp v/inspect :eurolog.bmw/loading-point-address)
                                        [:eurolog.bmw/unloading-point-address]
                                        (comp v/inspect :eurolog.bmw/unloading-point-address)
                                        [:eurolog.bmw/forwarder-address]
                                        (comp v/inspect :eurolog.bmw/unloading-point-address)

                                        [:eurolog.bmw/timestamp]
                                        (comp ductile.viewer/iso-datetime-viewer :eurolog.bmw/timestamp)
                                        [:eurolog.bmw/status-date]
                                        (comp ductile.viewer/iso-datetime-viewer :eurolog.bmw/status-date)
                                        [:eurolog.bmw/completion-date]
                                        (comp ductile.viewer/iso-datetime-viewer :eurolog.bmw/completion-date)
                                        [:eurolog.bmw/arrival-date]
                                        (comp ductile.viewer/iso-datetime-viewer :eurolog.bmw/arrival-date)
                                        [:eurolog.bmw/confirmed-pickup-date]
                                        (comp ductile.viewer/iso-datetime-viewer :eurolog.bmw/confirmed-pickup-date)

                                        [:eurolog.bmw/ship-from]
                                        (ductile.viewer/tabular-viewer [:eurolog.bmw/ship-from] {:font :tabular})
                                        [:eurolog.bmw/ship-to]
                                        (ductile.viewer/tabular-viewer [:eurolog.bmw/ship-to] {:font :tabular})

                                        [:eurolog.bmw/loading-point]
                                        (ductile.viewer/tabular-viewer [:eurolog.bmw/loading-point] {:font :tabular})
                                        [:eurolog.bmw/unloading-point]
                                        (ductile.viewer/tabular-viewer [:eurolog.bmw/unloading-point] {:font :tabular})

                                        [:edi-instruction/created-at]
                                        (comp ductile.viewer/iso-datetime-viewer :edi-instruction/created-at)

                                        [:omoda-jaecoo/type]
                                        (comp str :omoda-jaecoo/type)

                                        [:vehicle/vin]
                                        (ductile.viewer/tabular-viewer [:vehicle/vin] {:font :tabular})}}
                   (columns/->table-opts {:col-settings col-settings
                                          :available-cols available-cols
                                          :default-selected-cols column-order}))
            filtered-messages]]])))])
